import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
  MapPin, Loader2, ArrowLeft, Check,
  ClipboardList, Thermometer, CalendarCheck,
  Store, Receipt, Ticket, BookOpen, Users, ShoppingCart, Truck
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_ICONS = {
  tasks: ClipboardList,
  temperature_control: Thermometer,
  mhd_control: CalendarCheck,
  backshop: Store,
  general_writeoffs: Receipt,
  lotto: Ticket,
  unterweisungen: BookOpen,
  employee_management: Users,
  bestellungen: ShoppingCart,
  lieferung: Truck,
};

const MODULE_COLORS = {
  tasks: 'blue',
  temperature_control: 'cyan',
  mhd_control: 'orange',
  backshop: 'pink',
  general_writeoffs: 'red',
  lotto: 'yellow',
  unterweisungen: 'purple',
  employee_management: 'teal',
  bestellungen: 'green',
  lieferung: 'indigo',
};

const COLOR_CLASSES = {
  blue:   'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
  cyan:   'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400',
  orange: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400',
  pink:   'bg-pink-100 dark:bg-pink-900/40 text-pink-600 dark:text-pink-400',
  red:    'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
  yellow: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-400',
  purple: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400',
  teal:   'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400',
  green:  'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
  indigo: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400',
};

export const LocationModules = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(null);
  const [modules, setModules] = useState([]);
  const [togglingModule, setTogglingModule] = useState(null);

  useEffect(() => {
    fetchLocationModules();
  }, [locationId]);

  const fetchLocationModules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/admin/locations/${locationId}/modules`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLocation(response.data.location);
      setModules(response.data.modules);
    } catch (error) {
      console.error('Error fetching modules:', error);
      toast.error('Fehler beim Laden der Module');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = async (moduleId, currentlyEnabled) => {
    // Optimistic update
    setModules(prev =>
      prev.map(m => m.module_id === moduleId ? { ...m, is_enabled: !currentlyEnabled } : m)
    );
    setTogglingModule(moduleId);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/admin/locations/${locationId}/toggle-module`,
        { module_id: moduleId, enabled: !currentlyEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(currentlyEnabled ? 'Modul deaktiviert' : 'Modul aktiviert');
      window.dispatchEvent(new CustomEvent('modulesUpdated'));
    } catch (error) {
      // Revert on error
      setModules(prev =>
        prev.map(m => m.module_id === moduleId ? { ...m, is_enabled: currentlyEnabled } : m)
      );
      toast.error('Fehler beim Umschalten');
    } finally {
      setTogglingModule(null);
    }
  };

  const handleEnableAll = async () => {
    const disabledModules = modules.filter(m => !m.is_enabled);
    if (disabledModules.length === 0) {
      toast.info('Alle Module sind bereits aktiviert');
      return;
    }
    for (const module of disabledModules) {
      await handleToggleModule(module.module_id, false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const enabledCount = modules.filter(m => m.is_enabled).length;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-5">

        {/* Header */}
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/locations')} className="mb-3 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Zurück zu Standorten
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{location?.name}</h1>
              <p className="text-sm text-muted-foreground">Module aktivieren oder deaktivieren</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Badge variant="outline" className="text-sm">
                <Check className="h-3 w-3 mr-1 text-green-600" />
                {enabledCount} von {modules.length} aktiv
              </Badge>
              <Button size="sm" variant="outline" onClick={handleEnableAll}>
                Alle aktivieren
              </Button>
            </div>
          </div>
        </div>

        {/* Module Grid */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="module-grid">
          {modules.map((module) => {
            const IconComponent = MODULE_ICONS[module.module_id] || ClipboardList;
            const color = MODULE_COLORS[module.module_id] || 'blue';
            const colorClass = COLOR_CLASSES[color];
            const isToggling = togglingModule === module.module_id;

            return (
              <Card
                key={module.module_id}
                data-testid={`module-card-${module.module_id}`}
                className={`transition-all duration-200 ${
                  module.is_enabled
                    ? 'border-green-300 dark:border-green-700 bg-green-50/40 dark:bg-green-950/10'
                    : 'opacity-70'
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${colorClass}`}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm leading-tight">{module.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{module.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-border/50">
                    <Badge
                      variant={module.is_enabled ? 'default' : 'secondary'}
                      className={`text-xs ${module.is_enabled ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
                    >
                      {module.is_enabled ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                    <Switch
                      data-testid={`toggle-${module.module_id}`}
                      checked={module.is_enabled}
                      disabled={isToggling}
                      onCheckedChange={() => handleToggleModule(module.module_id, module.is_enabled)}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

      </div>
    </DashboardLayout>
  );
};

export default LocationModules;
